import type { App } from "@upsquad/server";
import type { CreateCommunityPayload, UpdateCommunityBrandingPayload } from "@upsquad/shared";
import type { EdenCall } from "@/src/common/lib/eden-base-query";
import { baseApi } from "@/src/common/services/base-api";

type GetMyCommunitiesResponse = App["~Routes"]["community"]["me"]["get"]["response"]["200"];
type CreateCommunityResponse = App["~Routes"]["community"]["post"]["response"]["200"];
type GetCommunityBySlugResponse = App["~Routes"]["community"]["by-slug"][":slug"]["get"]["response"]["200"];
type GetMembershipBySlugResponse = App["~Routes"]["community"]["by-slug"][":slug"]["membership"]["get"]["response"]["200"];
type UpdateCommunityBrandingResponse = App["~Routes"]["community"][":communityId"]["branding"]["patch"]["response"]["200"];

export const communityApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getMyCommunities: builder.query<GetMyCommunitiesResponse, void>({
			query: (): EdenCall<GetMyCommunitiesResponse> => (client) => client.community.me.get(),
			providesTags: ["Community"],
		}),
		getCommunityBySlug: builder.query<GetCommunityBySlugResponse, string>({
			query:
				(slug): EdenCall<GetCommunityBySlugResponse> =>
				(client) =>
					client.community["by-slug"]({ slug }).get(),
			providesTags: (_result, _error, slug) => [{ type: "Community", id: slug }],
		}),
		getMyMembershipBySlug: builder.query<GetMembershipBySlugResponse, string>({
			query:
				(slug): EdenCall<GetMembershipBySlugResponse> =>
				(client) =>
					client.community["by-slug"]({ slug }).membership.get(),
			providesTags: (_result, _error, slug) => [{ type: "Community", id: `membership-${slug}` }],
		}),
		createCommunity: builder.mutation<CreateCommunityResponse, CreateCommunityPayload>({
			query:
				(payload): EdenCall<CreateCommunityResponse> =>
				(client) =>
					client.community.post(payload),
			invalidatesTags: ["Community"],
		}),
		updateCommunityBranding: builder.mutation<
			UpdateCommunityBrandingResponse,
			{ communityId: string; payload: UpdateCommunityBrandingPayload }
		>({
			query:
				({ communityId, payload }): EdenCall<UpdateCommunityBrandingResponse> =>
				(client) =>
					client.community({ communityId }).branding.patch(payload),
			invalidatesTags: (_result, _error, { communityId }) => [{ type: "Community", id: communityId }, "PublicCommunity"],
		}),
	}),
});

export const {
	useGetMyCommunitiesQuery,
	useGetCommunityBySlugQuery,
	useGetMyMembershipBySlugQuery,
	useCreateCommunityMutation,
	useUpdateCommunityBrandingMutation,
} = communityApi;
